import type { CarTrip, CarTripDay, CarTripDayCreate } from '../types/CarTripTypes';

export type CarTripDayDraft = CarTripDayCreate & {
  id?: number;
  avoidFerries?: boolean;
  avoidToll?: boolean;
  fuelUsed?: number;
  totalCost?: number;
};

export const createDraftDay = (date = new Date().toISOString().slice(0, 10)): CarTripDayDraft => ({
  date,
  originCity: '',
  destinationCity: '',
  waypoints: [],
  fuelPrice: 0,
  distance: 0,
  duration: 0,
  formattedDuration: '',
  legDistances: [],
  tollRoadCosts: [],
  roadNumbers: [],
  avoidFerries: true,
  avoidToll: false,
});

export const addDraftDay = (
  days: CarTripDayDraft[],
  date?: string,
): CarTripDayDraft[] => [...days, createDraftDay(date)];

export const appendDraftDayAfter = (
  days: CarTripDayDraft[],
): CarTripDayDraft[] => {
  const lastDate = days[days.length - 1]?.date;
  const nextDate = lastDate ? new Date(`${lastDate}T12:00:00`) : new Date();
  if (Number.isNaN(nextDate.getTime())) return [...days, createDraftDay()];
  nextDate.setDate(nextDate.getDate() + 1);
  return [...days, createDraftDay(nextDate.toISOString().slice(0, 10))];
};

export const updateDraftDay = (
  days: CarTripDayDraft[],
  index: number,
  patch: Partial<CarTripDayDraft>,
): CarTripDayDraft[] => days.map((day, dayIndex) => (
  dayIndex === index ? { ...day, ...patch } : day
));

export const removeDraftDay = (
  days: CarTripDayDraft[],
  index: number,
): CarTripDayDraft[] => (
  days.length <= 1 ? days : days.filter((_, dayIndex) => dayIndex !== index)
);

export const reconcileDraftDays = (
  existingDays: CarTripDay[],
  draftDays: CarTripDayDraft[],
) => {
  const existingById = new Map(existingDays.map((day) => [day.id, day]));
  const retainedIds = new Set(draftDays.reduce<number[]>(
    (ids, day) => day.id == null ? ids : [...ids, day.id],
    [],
  ));
  return {
    updates: draftDays
      .filter((day): day is CarTripDayDraft & { id: number } => day.id != null && existingById.has(day.id))
      .map((day) => ({ existing: existingById.get(day.id)!, draft: day })),
    additions: draftDays.filter((day) => day.id == null),
    deletions: existingDays.filter((day) => !retainedIds.has(day.id)),
  };
};

export const formatCarTripDaysCount = (
  count: number,
  language: string,
  singularLabel: string,
  countTemplate: string,
): string => {
  if (language.startsWith('ru')) {
    const lastTwo = count % 100;
    const last = count % 10;
    const form = last === 1 && lastTwo !== 11
      ? singularLabel.toLowerCase()
      : last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)
        ? 'дня'
        : 'дней';
    return `${count} ${form}`;
  }
  return count === 1
    ? `${count} ${singularLabel.toLowerCase()}`
    : countTemplate.replace('{n}', String(count));
};

const tripDate = (trip: CarTrip): string => (
  trip.dateTo
  ?? (
    trip.days.reduce((latest, day) => day.date > latest ? day.date : latest, '')
    || trip.dateFrom
    || ''
  )
);

export const filterAndSortCarTrips = (
  trips: CarTrip[],
  filters: string[],
  knownTags: string[],
): CarTrip[] => {
  const tagSet = new Set(knownTags.map((tag) => tag.toLowerCase()));
  const years = filters.filter((filter) => /^\d{4}$/.test(filter));
  const tags = filters
    .filter((filter) => !/^\d{4}$/.test(filter) && tagSet.has(filter.toLowerCase()))
    .map((filter) => filter.toLowerCase());
  const text = filters
    .filter((filter) => !/^\d{4}$/.test(filter) && !tagSet.has(filter.toLowerCase()))
    .map((filter) => filter.toLowerCase());

  return trips
    .filter((trip) => {
      if (years.length && !trip.days.some((day) => years.includes(day.date.slice(0, 4)))) return false;
      const tag = (trip.tag ?? '').toLowerCase();
      if (tags.length && !tags.some((filter) => tag === filter || tag.includes(filter))) return false;
      const dayPlaces = trip.days.reduce<string[]>((places, day) => [
        ...places,
        day.originCity,
        day.destinationCity,
        ...(day.waypoints ?? []),
      ], []);
      const haystack = [trip.carMake, trip.tag, ...dayPlaces]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return text.every((filter) => haystack.includes(filter));
    })
    .sort((first, second) => tripDate(second).localeCompare(tripDate(first)));
};
